import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Paper,
  Button,
  Typography,
  FormControlLabel,
  Checkbox
} from "@material-ui/core";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import {
  useDebounceEffect,
  useLocalStorageState,
  useUpdateEffect
} from "ahooks";
import flatten from "lodash/flatten";
import * as $ from "jquery";
import useStyles from "@/styles/groupsStyle";
import { deviceModifyAPI } from "@/apis/deviceModifyAPI";
import HasPermission from "@/components/commons/HasPermission";
import ImportDevicesDialog from "@/components/groups/ImportDevicesDialog";
import LoadingIndicator from "@/components/commons/LoadingIndicator";
import SingleSelector from "@/components/SingleSelector";
import SearchInput from "@/components/SearchInput";
import DeviceListTable from "@/components/device/DeviceListTable";
import { IconDevices } from "@/components/Icon";
import { FormItem } from "@/components/Form";
import { useTranslation } from "@/langs/useTranslation";
import { getCurrentCompanyId, useGlobalState } from "@/GlobalProvider";
import { toast } from "@/apis/request";
import {
  assignToGroupsApi,
  transferGroupsApi,
  deleteDeviceFromGroupApi
} from "@/apis/groupsApi";
import { tagBindDeviceAPI } from "@/apis/tagBindDeviceAPI";
import { useGroupDeviceDispatch, useGroupDeviceState } from "./Provider";
import modalConfirm from "@/components/Modal/modalConfirm";
import ViewGroupTokenModal from "./ViewGroupTokenModal";
import SendCommandModal from "./SendCommandModal";
import TransferGroupModal from "./Category/TransferGroupModal";
import TransferGroupTagModal from "./TransferGroupTagModal";
import SetAlarmModal from "./SetAlarmModal";
import SetScheduleModal from "./SetScheduleModal";
import TagList from "@/components/FormFields/TagList";
import { statisticDevicesApi } from "@/apis/apiCollection";
import showLoadingIndicator, {
  hideLoadingIndicator
} from "@/components/Modal/showLoadingIndicator";
import { getQueryTextFromParams } from "@/components/dashboard/OfflineDays";
import StoreDetailModal from "./StoreDetail";
import { getSearchTypes } from "@/components/commons/SearchBar";
import { parseJSON } from "@/helpers/utils";
import { APP_ENV } from "@/APP_ENV";

const GroupsDevices = () => {
  const { t, userLanguage, langConf } = useTranslation("dashboard");
  const classes = useStyles();
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [taskSchedule, setTaskSchedule] = useState([]);
  const [crossType, setCrossType] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [transferGroupModalOpen, setTransferGroupModalOpen] = useState(false);
  const [transferTagModalOpen, setTransferTagModalOpen] = useState(false);
  const [groupTokenModalOpen, setGroupTokenModalOpen] = useState(false);
  const [sendCommandModalOpen, setSendCommandModalOpen] = useState(false);
  const [alarmModalOpen, setAlarmModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [storeDetailModalOpen, setStoreDetailModalOpen] = useState(false);
  const [loadingTable, setLoadingTable] = useState(null);
  const deviceTableRef = useRef(null);
  const selectedRowIdsRef = useRef([]);
  const selectedRowsRef = useRef([]);
  const isReset = useRef(true);
  const shouldReset = useRef(false);

  // prettier-ignore
  const { currentGroup, loadingGroup, currentTag, filterBy, filterByMode } = useGroupDeviceState();
  const { setFilterBy, reloadCategoryTree } = useGroupDeviceDispatch();
  const { currentCompanyId } = useGlobalState();
  const { location, push } = useHistory();

  const showTransferBtn = !filterByMode;
  const getTable = () => deviceTableRef.current;

  const query = useMemo(
    () =>
      new URLSearchParams(location.search || (filterByMode ? filterBy : "")),
    [location.search, filterByMode, filterBy]
  );

  const [lastGroupQueryParams, setLastGroupQueryParams] = useLocalStorageState(
    "__lastGroupQueryParams",
    { defaultValue: {} }
  );

  const currentGroupId = !currentGroup
    ? []
    : Array.isArray(currentGroup.id)
      ? currentGroup.id
      : [currentGroup.id];

  // prettier-ignore
  const initialValues = {
    deviceStatus: (filterByMode ? query.get("online_status") : lastGroupQueryParams.deviceStatus) || undefined,
    osType: (filterByMode ? query.get("os_type") : lastGroupQueryParams.osType) || undefined,
    kioskMode: filterByMode ? (query.get("kiosk_mode") ? Number(query.get("kiosk_mode")) : undefined) : lastGroupQueryParams.kioskMode,
    nubisCast: filterByMode ? (query.get("nubisCast") ? Number(query.get("nubisCast")) : undefined) : lastGroupQueryParams.nubisCast,
    nubisVersion: (filterByMode ? query.get("nubis_version") : lastGroupQueryParams.nubisVersion) || undefined,
    tagIds: (filterByMode ? query.get("tagIds")?.split(",") : lastGroupQueryParams.tagIds) || [],
    latestTaskStatus: (filterByMode ? query.get("latest_task_status") : lastGroupQueryParams.latestTaskStatus) || undefined,
    lastUpdatedStatus: (filterByMode ? query.get("last_updated_status") : lastGroupQueryParams.lastUpdatedStatus) || undefined,
    searchValue: ""
  };

  const { values, handleChange, handleSubmit, resetForm } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable(false, true);
    }
  });

  const getSearchParams = () => {
    const formValues = isReset.current ? initialValues : values;

    // prettier-ignore
    const params = {
      query,
      filterByMode,
      searchValue: formValues.searchValue || (filterByMode ? query.get("search") : ""),
      groupId: !filterByMode ? currentGroupId : undefined,
      deviceStatus: formValues.deviceStatus || "",
      nubisVersion: formValues.nubisVersion || "",
      tagIds: !filterByMode ? (currentTag ? [currentTag.id] : formValues.tagIds) : formValues.tagIds,
      osType: formValues.osType || "",
      kioskMode: formValues.kioskMode ?? undefined,
      nubisCast: formValues.nubisCast ?? undefined,
      latestTaskStatus: formValues.latestTaskStatus || "",
      lastUpdatedStatus: formValues.lastUpdatedStatus || "",
    };

    if (!filterByMode) {
      setLastGroupQueryParams(params);
    }

    return params;
  };

  const reloadTableWithGroupId = resetPage => {
    isReset.current = false;
    getTable()?.reload(resetPage);
  };

  const resetTableWithGroupId = async () => {
    try {
      isReset.current = true;
      // ensure only triggered once on page load
      if (loadingTable === null) {
        setLoadingTable(true);
      }
      await getTable()?.load();
    } finally {
      if (loadingTable !== false) {
        setLoadingTable(false);
      }
    }
  };

  const handleReloadDataTable = (resetQuery, resetPage) => {
    if (resetQuery) {
      resetTableWithGroupId();
    } else {
      reloadTableWithGroupId(resetPage);
    }
    setCrossType(1);
  };

  const handleCheckboxesState = state => {
    try {
      const api = getTable()?.api();
      api.column(0).checkboxes.select(false);
      api.cells().checkboxes.disable(state);
      $(api.table().header())
        .find(".dt-checkboxes-select-all input[type=checkbox]")
        .prop("disabled", state);
    } catch (e) {
      console.error(e);
    }
  };

  // const handleCrossTypeChange = e => {
  //   const value = e.target.value;
  //   setCrossType(value);
  //   handleCheckboxesState(value === 2);
  // };

  const handleCrossTypeChange2 = () => {
    setCrossType(prev => {
      const newType = prev === 1 ? 2 : 1;
      handleCheckboxesState(newType === 2);
      return newType;
    });
  };

  const getDisplayTextByKey = key => {
    const map = {
      search: t({ defaultMessage: "关键字" }),
      nubis_version: t({ defaultMessage: "Nubis版本" }),
      ora_version: t({ defaultMessage: "Ora版本" }),
      online_status: t({ defaultMessage: "在线状态" }),
      os_type: t({ defaultMessage: "作业系统版本" }),
      devices_offline_days: t({ defaultMessage: "离线设备状态" }),
      device_model: t({ defaultMessage: "硬体型号" }),
      realtime_type: t({ defaultMessage: "设备健康状态" }),
      realtime_range: t({ defaultMessage: "设备健康状态区间" }),
      device_group: t({ defaultMessage: "设备门店" }),
      task_type: t({ defaultMessage: "任务类型" }),
      task_status: t({ defaultMessage: "任务状态" }),
      kiosk_mode: t({ defaultMessage: "Kiosk模式" }),
      searchType: t({ defaultMessage: "搜索类型" })
    };
    return map[key] || key;
  };

  const getDisplayValueByKey = (value, key) => {
    if (key === "devices_offline_days") {
      return getQueryTextFromParams(value);
    }
    if (key === "realtime_type") {
      const expr = value.match(/(cpu_usage|ram_usage|storage_usage)(.*)/);
      if (expr) {
        // prettier-ignore
        return `= ${t(`healthStatus.${expr[1]}`)} ${t(`healthStatus.${expr[2]}`)}`;
      }
    }
    if (key === "kiosk_mode") {
      if (value === "1") {
        return `= ${t({ defaultMessage: "开启" })}`;
      }
      return `= ${t({ defaultMessage: "关闭" })}`;
    }
    if (key === "searchType") {
      return `= ${getSearchTypes(t).find(item => item.id === value)?.name ?? value
        }`;
    }
    return "= " + value;
  };

  const filterByDisplayText = useMemo(() => {
    if (!filterByMode) return "";
    const textStack = [];
    for (const [key, value] of query) {
      if (
        key === "groupIds" ||
        key === "tagIds" ||
        key === "hasSubCompanies" ||
        key === "full_search"
      ) {
        continue;
      }
      // prettier-ignore
      textStack.push(`${getDisplayTextByKey(key)} ${getDisplayValueByKey(value, key)}`);
    }
    return textStack.join(", ");
  }, [filterByMode, filterBy, userLanguage]);

  const importHandler = async () => {
    let res = await deviceModifyAPI("group", currentGroupId, undefined);
    if (res) {
      setTaskSchedule(res);
      setOpenImportDialog(true);
    }
  };

  // const handleSearch = e => {
  //   // groupTable.search(e.target.value).draw();
  //   handleReloadDataTable();
  // };

  const handleTagChange = () => {
    reloadCategoryTree();
  };

  const handleLoaded = json => {
    setTotalCount(json.recordsTotal);
  };

  const resetQuery = forceReset => {
    // prettier-ignore
    if (shouldReset.current) {
      initialValues.deviceStatus = filterByMode && !forceReset ? initialValues.deviceStatus : undefined;
      initialValues.nubisVersion = filterByMode && !forceReset ? initialValues.nubisVersion : undefined;
      initialValues.tagIds = filterByMode && !forceReset ? initialValues.tagIds : [];
      initialValues.osType = filterByMode && !forceReset ? initialValues.osType : undefined;
      initialValues.kioskMode = filterByMode && !forceReset ? initialValues.kioskMode : undefined;
      initialValues.nubisCast = filterByMode && !forceReset ? initialValues.nubisCast : undefined;
      initialValues.latestTaskStatus = filterByMode && !forceReset ? initialValues.latestTaskStatus : undefined;
      initialValues.lastUpdatedStatus = filterByMode && !forceReset ? initialValues.lastUpdatedStatus : undefined;

      resetForm({
        values: initialValues
      });
    }
    shouldReset.current = true;
    handleReloadDataTable(true);
  };

  const getSelectedRowIds = async () => {
    let selectedRowIds = [];
    if (crossType === 2) {
      // selectedRowIds = getTable()
      //   ?.getAll()
      //   .map(item => item.id);
      const groupIds = !currentGroup
        ? undefined
        : currentGroup.checkedGroups?.length > 0
          ? currentGroup.checkedGroups.map(group => group.id)
          : currentGroup.id
            ? [currentGroup.id]
            : undefined;
      try {
        showLoadingIndicator();
        const res = await statisticDevicesApi({
          group_ids: groupIds,
          tag_ids: currentTag?.id ? [currentTag?.id] : undefined
        });
        selectedRowIds = res.stat.devices.map(item => item.id);
        selectedRowsRef.current = res.stat.devices;
      } finally {
        hideLoadingIndicator();
      }
    } else {
      selectedRowIds = getTable()?.getSelected();
    }
    if (selectedRowIds && selectedRowIds.length) {
      selectedRowIdsRef.current = selectedRowIds;
      return selectedRowIds;
    }
    toast.info(t({ defaultMessage: "未选择设备" }));
  };

  const getSelectedRows = () => {
    let selectedRows = [];
    if (crossType === 2) {
      // selectedRows = getTable()?.getAll();
      selectedRows = selectedRowsRef.current;
    } else {
      selectedRows = getTable()?.getSelectedRows();
    }
    return selectedRows;
  };

  const checkIfDevicesInCurrentCompany = async () => {
    const curCompanyId = await getCurrentCompanyId();
    const devicesCompanyIds = getSelectedRows().map(row =>
      row.companies ? parseJSON(row.companies).map(item => item.id) : null
    );
    for (const companyIds of devicesCompanyIds) {
      if (!companyIds) return false;
      if (companyIds.includes(curCompanyId)) {
        return true;
      }
    }
    return false;
  };

  const handleTransferGroup = async () => {
    if (await getSelectedRowIds()) {
      if (filterByMode && !(await checkIfDevicesInCurrentCompany())) {
        return modalConfirm({
          title: t({ defaultMessage: "提示信息" }),
          content: t({ defaultMessage: "您不能转移不在当前公司的设备。" }),
          showCancel: false
        });
      }
      setTransferGroupModalOpen(true);
    }
  };

  const handleTransferTag = async () => {
    if (await getSelectedRowIds()) {
      setTransferTagModalOpen(true);
    }
  };

  const handleDeleteTag = async () => {
    if (await getSelectedRowIds()) {
      modalConfirm({
        title: t({ defaultMessage: "提示信息" }),
        content: t({ defaultMessage: "确定要移除吗？" }),
        onConfirm: async () => {
          let res = await deviceModifyAPI(
            "tag",
            undefined,
            flatten(getSelectedRows().map(item => item.tags.map(tag => tag.id)))
          );
          if (res) {
            res = await tagBindDeviceAPI([], selectedRowIdsRef.current);
            if (res?.message === "success") {
              handleReloadDataTable();
              reloadCategoryTree();
              toast.success(t({ defaultMessage: "操作成功" }));
            }
            setTransferTagModalOpen(false);
          }
        }
      });
    }
  };

  const handleTransferGroupConfirm = (checkedKeys, unInstallApp) => {
    const checkPass = () => {
      if (Array.isArray(currentGroup.id)) {
        return false;
        // return currentGroup.id.includes(checkedKeys[0]);
      }
      return checkedKeys[0] === currentGroup.id;
    };

    const checkHandler = () => {
      if (currentGroup && checkPass()) {
        const err = new Error(t({ defaultMessage: "不可转移到同一门店" }));
        toast.info(err.message);
        return Promise.reject(err);
      }
      return Promise.resolve();
    };

    const getGroupIdFromDevices = () => {
      return [
        ...new Set(
          flatten(
            getSelectedRows().map(device =>
              device.groups.map(group => group.id)
            )
          )
        )
      ];
    };

    const getSelectedGroupIds = () => {
      let groupIds = [];
      const isNoneGroup = !currentGroup || currentGroup.isRoot;
      if (isNoneGroup) {
        groupIds = getGroupIdFromDevices();
      } else {
        groupIds = flatten([currentGroup.id]);
        // 当前公司id作为仓储(default)节点id
        groupIds = groupIds.filter(id => id !== currentCompanyId);
        if (!groupIds.length) {
          groupIds = getGroupIdFromDevices();
        }
      }
      return groupIds;
    };

    const deleteHandler = () => {
      return new Promise((resolve, reject) => {
        (async () => {
          try {
            const groupIds = getSelectedGroupIds();
            // 如果groupIds为空，说明它不属于任何分组，因此不需要删除
            if (!groupIds.length) {
              resolve();
              return;
            }

            let res = await deviceModifyAPI("group", undefined, groupIds);

            if (res) {
              res = await deleteDeviceFromGroupApi(
                selectedRowIdsRef.current,
                groupIds
              );
              if (res && res.status && res.status === "success") {
                resolve();
              }
            }
          } catch (e) {
            reject(e);
          }
        })();
      });
    };

    const addHandler = () => {
      return new Promise((resolve, reject) => {
        (async () => {
          try {
            // default为仓储节点，表示不属于任何门店，只做删除处理
            if (checkedKeys[0] === currentCompanyId) {
              resolve();
              return;
            }
            let res = await deviceModifyAPI("group", checkedKeys, undefined);
            if (res) {
              res = await assignToGroupsApi(
                selectedRowIdsRef.current,
                checkedKeys[0]
              );
              if (res && res.status && res.status === "success") {
                resolve();
              }
            }
          } catch (e) {
            reject(e);
          }
        })();
      });
    };

    const addHandlerV2 = () => {
      return new Promise((resolve, reject) => {
        (async () => {
          try {
            // 这里代码有bug currentCompanyId目前只有切换团队才会有值，不然是undefined，然后现在允许转移到仓储了
            // default为仓储节点，表示不属于任何门店，只做删除处理
            // if (checkedKeys[0] === currentCompanyId) {
            //   console.log('????tiaoguole', checkedKeys, currentCompanyId)
            //   resolve();
            //   return;
            // }
            let res = await deviceModifyAPI("group", checkedKeys, undefined);
            if (res) {
              const oldGroupIds = getSelectedGroupIds();
              res = await transferGroupsApi(
                selectedRowIdsRef.current,
                checkedKeys[0],
                oldGroupIds,
                unInstallApp
              );
              if (res && res.status && res.status === "success") {
                resolve();
              }
            }
          } catch (e) {
            reject(e);
          }
        })();
      });
    };

    const startHandler = async () => {
      await checkHandler();
      // await deleteHandler();
      // await addHandler();
      await addHandlerV2();
    };

    return startHandler().then(() => {
      setTransferGroupModalOpen(false);
      handleReloadDataTable();
      reloadCategoryTree();
    });
  };

  const handleTransferTagConfirm = async checkedKeys => {
    let res = await deviceModifyAPI("tag", checkedKeys, undefined);
    if (res) {
      res = await tagBindDeviceAPI(checkedKeys, selectedRowIdsRef.current);
      if (res?.message === "success") {
        handleReloadDataTable();
        reloadCategoryTree();
        toast.success(t({ defaultMessage: "操作成功" }));
      }
      setTransferTagModalOpen(false);
    }
  };

  const handleExport = () => {
    getTable()?.export();
  };

  const handleViewToken = () => {
    setGroupTokenModalOpen(true);
  };

  const handleSendCommand = async () => {
    if (await getSelectedRowIds()) {
      // if (crossType === 2) {
      //   setSendCommandModalOpen(true);
      //   return;
      // }

      const freeDevices = getSelectedRows().filter(
        row => row.nubis_version === "nubis_free"
      );
      if (selectedRowIdsRef.current.length > 1 && freeDevices.length > 0) {
        modalConfirm({
          title: t({ defaultMessage: "温馨提示" }),
          content: t(
            {
              defaultMessage:
                "选择设备中包含{num}台Nubis Free版本，将无法进行设备命令。"
            },
            { num: freeDevices.length }
          ),
          showCancel: false,
          onConfirm: () => {
            // const api = getTable()?.api();
            // const rows = getTable()?.getAll();
            // const freeDeviceIndexes = freeDevices.map(item =>
            //   rows.findIndex(row => row.id === item.id)
            // );
            // api
            //   .cells(
            //     api
            //       .rows()
            //       .indexes()
            //       .filter(idx => freeDeviceIndexes.includes(idx)),
            //     0
            //   )
            //   .checkboxes.select(false);
            // setTimeout(() => {
            //   await getSelectedRowIds();
            //   setSendCommandModalOpen(true);
            // });
          }
        });
      } else {
        setSendCommandModalOpen(true);
      }
    }
  };

  const handleStoreDetail = () => {
    setStoreDetailModalOpen(true);
  };

  useEffect(() => {
    if (query.size) {
      setFilterBy(query.toString());
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (!filterByMode) {
      push(location.pathname);
    }
  }, [filterByMode]);

  useDebounceEffect(
    () => {
      if (currentGroup || currentTag || filterBy) {
        resetQuery();
      }
    },
    [currentGroup && currentGroup.id, currentTag, filterBy, currentCompanyId],
    { wait: 20 }
  );

  return (
    <div className={classes.main}>
      <div className={classes.tableAction}>
        <HasPermission permission="device_monitoring_export">
          <Button
            variant="outlined"
            className={classes.iconWText}
            onClick={handleExport}
          >
            {t({ defaultMessage: "导出" })}
          </Button>
        </HasPermission>
        {currentGroup &&
          currentGroup.key !== "all" &&
          !currentGroup.checkedGroups && (
            <>
              <Button
                variant="outlined"
                className={classes.iconWText}
                onClick={() =>
                  !loadingGroup && !loadingTable && handleStoreDetail()
                }
              >
                {t({ defaultMessage: "门店信息" })}
              </Button>
              <HasPermission permission="device_monitoring_token">
                <Button
                  variant="outlined"
                  className={classes.iconWText}
                  onClick={handleViewToken}
                >
                  {t({ defaultMessage: "门店配置信息" })}
                </Button>
              </HasPermission>
              {/* <HasPermission permission="assign_devices_to_groups"> */}
              <Button
                variant="outlined"
                className={classes.iconWText}
                onClick={() =>
                  !loadingGroup && !loadingTable && importHandler()
                }
              >
                {t({ defaultMessage: "导入设备" })}
              </Button>
              {/* </HasPermission> */}
            </>
          )}
      </div>
      <Paper elevation={0} className={classes.deviceInGroup}>
        <form onSubmit={handleSubmit}>
          <div className={classes.tableTop}>
            {filterByMode ? (
              <div>
                <div className={classes.queryKeyword}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    component="span"
                  >
                    {t({ defaultMessage: "查询结果：" })}
                  </Typography>
                  <Typography variant="body1" component="span">
                    <IconDevices color="primary" style={{ marginRight: 5 }} />
                    {totalCount}
                    {t({ defaultMessage: "台" })}
                  </Typography>
                </div>
                <div className={classes.queryKeyword}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    component="span"
                  >
                    {t({ defaultMessage: "查询条件：" })}
                  </Typography>
                  <Typography variant="body1" component="span">
                    {filterByDisplayText}
                  </Typography>
                </div>
              </div>
            ) : (
              <div>
                {(currentGroup || currentTag) && (
                  <>
                    <span className={classes.groupName}>
                      {currentGroup ? currentGroup.title : currentTag.name}
                    </span>
                    <span className={classes.groupCount}>
                      <IconDevices color="primary" style={{ marginRight: 5 }} />
                      {/* {currentGroup
                          ? currentGroup.checkedGroups
                            ? currentGroup.checkedGroups.reduce(
                                (p, v) => p + v.count,
                                0
                              )
                            : currentGroup.count
                          : currentTag.devices_count} */}
                      {totalCount}
                      {t({ defaultMessage: "台" })}
                    </span>
                  </>
                )}
              </div>
            )}
            {!filterByMode && (
              <SearchInput
                placeholder={t({ defaultMessage: "搜索" })}
                name="searchValue"
                icon={null}
                disabled={crossType === 2}
                value={values.searchValue}
                onChange={handleChange}
              // onPressEnter={handleSearch}
              />
            )}
          </div>

          <div className={classes.tableQueryForm}>
            <FormItem>
              <SingleSelector
                placeholder={t({ defaultMessage: "选择状态" })}
                options={[
                  { name: t({ defaultMessage: "在线" }), id: "online" },
                  { name: t({ defaultMessage: "离线" }), id: "offline" }
                ]}
                clearable
                disabled={crossType === 2}
                name="deviceStatus"
                initialValue=""
                value={values.deviceStatus}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem>
              <SingleSelector
                placeholder="OS"
                options={[
                  { name: "Android", id: "android" },
                  { name: "Windows", id: "windows" },
                  { name: "Linux", id: "linux" }
                ]}
                clearable
                disabled={crossType === 2}
                name="osType"
                initialValue=""
                value={values.osType}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem>
              <SingleSelector
                placeholder={t({ defaultMessage: "Kiosk模式" })}
                options={[
                  { name: t({ defaultMessage: "开启" }), id: 1 },
                  { name: t({ defaultMessage: "关闭" }), id: 0 }
                ]}
                clearable
                disabled={crossType === 2}
                name="kioskMode"
                value={values.kioskMode}
                onChange={handleChange}
              />
            </FormItem>
            {APP_ENV.REACT_APP_CMS_ENABLED &&
              <FormItem>
                <SingleSelector
                  placeholder={t({ defaultMessage: "{nubis} Cast" }, { nubis: langConf.deviceCenter?.nubisText || "Nubis" })}
                  options={[
                    { name: t({ defaultMessage: "开启" }), id: 1 },
                    { name: t({ defaultMessage: "关闭" }), id: 0 }
                  ]}
                  clearable
                  disabled={crossType === 2}
                  name="nubisCast"
                  value={values.nubisCast}
                  onChange={handleChange}
                />
              </FormItem>
            }
            {/* <FormItem>
              <SingleSelector
                placeholder={t({ defaultMessage: "{nubis}版本" }, { nubis: langConf.deviceCenter?.nubisText || "Nubis" })}
                options={[
                  { name: "Pro", id: "nubis_pro" },
                  { name: "Free", id: "nubis_free" }
                ]}
                clearable
                disabled={crossType === 2}
                name="nubisVersion"
                initialValue=""
                value={values.nubisVersion}
                onChange={handleChange}
              />
            </FormItem> */}
            {!currentTag && (
              <FormItem>
                <TagList
                  disabled={crossType === 2}
                  name="tagIds"
                  value={values.tagIds}
                  onChange={handleChange}
                />
              </FormItem>
            )}
            <FormItem>
              <SingleSelector
                placeholder={t({ defaultMessage: "最后执行状态" })}
                options={[
                  { name: t({ defaultMessage: "已完成" }), id: "completed" },
                  { name: t({ defaultMessage: "失败" }), id: "failed" },
                  { name: t({ defaultMessage: "进行中" }), id: "processing" }
                ]}
                clearable
                disabled={crossType === 2}
                name="latestTaskStatus"
                initialValue=""
                value={values.latestTaskStatus}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem>
              <SingleSelector
                placeholder={t({ defaultMessage: "{ora}最后更新状态" }, { ora: langConf.deviceCenter?.oraText || "Ora" })}
                options={[
                  { name: t({ defaultMessage: "已完成" }), id: "completed" },
                  { name: t({ defaultMessage: "失败" }), id: "failed" },
                  { name: t({ defaultMessage: "安装中" }), id: "install" },
                  { name: t({ defaultMessage: "下载中" }), id: "download" }
                ]}
                clearable
                disabled={crossType === 2}
                name="lastUpdatedStatus"
                initialValue=""
                value={values.lastUpdatedStatus}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={crossType === 2}
                style={{ marginRight: 8 }}
              >
                {t({ defaultMessage: "查询" })}
              </Button>
              <Button
                variant="outlined"
                disabled={crossType === 2}
                onClick={() => resetQuery(true)}
              >
                {t({ defaultMessage: "重置" })}
              </Button>
            </FormItem>
          </div>
        </form>
        {(loadingGroup || loadingTable) && <LoadingIndicator />}
        <div
          className={classes.tableWrap}
          style={{ visibility: loadingGroup || loadingTable ? "hidden" : "" }}
        >
          <DeviceListTable
            ref={deviceTableRef}
            getSearchParams={getSearchParams}
            onTagChange={handleTagChange}
            onLoaded={handleLoaded}
            showCheckAll={false}
            reloadTable={reloadTableWithGroupId}
            // checkAllDisabled={crossType === 2}
            footer={
              <>
                {/* <SingleSelector
                  placeholder={t({ defaultMessage: "模式" })}
                  options={
                    currentGroup && currentGroup.key === "all"
                      ? [{ name: t({ defaultMessage: "跨页" }), id: 1 }]
                      : [
                          { name: t({ defaultMessage: "跨页" }), id: 1 },
                          { name: t({ defaultMessage: "门店" }), id: 2 }
                        ]
                  }
                  style={{ marginRight: 0 }}
                  value={crossType}
                  onChange={handleCrossTypeChange}
                /> */}
                {(currentTag ||
                  (currentGroup && currentGroup.key !== "all")) && (
                    <FormControlLabel
                      style={{ marginLeft: 5 }}
                      control={
                        <Checkbox
                          checked={crossType === 2}
                          onChange={handleCrossTypeChange2}
                        />
                      }
                      label={
                        currentTag
                          ? t({
                            id: "devicesList@1",
                            defaultMessage: "选择全部设备"
                          })
                          : t({
                            id: "devicesList@2",
                            defaultMessage: "选择全部设备"
                          })
                      }
                    />
                  )}
                {!filterByMode && (
                  <>
                    <Button variant="outlined" onClick={handleTransferTag}>
                      {t({ defaultMessage: "贴标签" })}
                    </Button>
                    <Button variant="outlined" onClick={handleDeleteTag}>
                      {t({ defaultMessage: "移除标签" })}
                    </Button>
                  </>
                )}
                {showTransferBtn && (
                  <Button variant="outlined" onClick={handleTransferGroup}>
                    {t({ defaultMessage: "转移门店" })}
                  </Button>
                )}
                <HasPermission permission="device_control_group_command">
                  <Button variant="outlined" onClick={handleSendCommand}>
                    {t({ defaultMessage: "设备命令" })}
                  </Button>
                </HasPermission>
              </>
            }
          />
        </div>
        <TransferGroupModal
          open={transferGroupModalOpen}
          selectedGroupId={currentGroup?.id}
          onClose={() => setTransferGroupModalOpen(false)}
          onConfirm={handleTransferGroupConfirm}
        />
        <TransferGroupTagModal
          open={transferTagModalOpen}
          onClose={() => setTransferTagModalOpen(false)}
          onConfirm={handleTransferTagConfirm}
        />
        <ImportDevicesDialog
          groupId={currentGroup?.id || ""}
          open={openImportDialog}
          setOpen={setOpenImportDialog}
          handleReloadDataTable={handleReloadDataTable}
          taskSchedule={taskSchedule}
          t={t}
        />
        <ViewGroupTokenModal
          groupId={currentGroup?.id}
          groupName={currentGroup?.name}
          open={groupTokenModalOpen}
          onClose={() => setGroupTokenModalOpen(false)}
        />
        <SendCommandModal
          crossSelect={crossType === 2}
          getSelectedDevices={getSelectedRows}
          open={sendCommandModalOpen}
          onClose={() => setSendCommandModalOpen(false)}
          onSetAlarm={() => {
            setSendCommandModalOpen(false);
            setAlarmModalOpen(true);
          }}
          onSetSchedule={() => {
            setSendCommandModalOpen(false);
            setScheduleModalOpen(true);
          }}
        />
        <SetAlarmModal
          crossSelect={crossType === 2}
          getSelectedDevices={getSelectedRows}
          open={alarmModalOpen}
          onClose={() => setAlarmModalOpen(false)}
        />
        <SetScheduleModal
          crossSelect={crossType === 2}
          getSelectedDevices={getSelectedRows}
          open={scheduleModalOpen}
          onClose={() => setScheduleModalOpen(false)}
        />
        <StoreDetailModal
          groupId={currentGroup?.id}
          open={storeDetailModalOpen}
          onClose={() => {
            setStoreDetailModalOpen(false);
          }}
        />
      </Paper>
    </div>
  );
};

export default GroupsDevices;
